import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRightIcon } from "@radix-ui/react-icons";

import { useDocumentTitle } from "@/hooks/use-document-title";
import { useCMWFeatureFlagsQuery } from "@/api/cmw-feature-flags";
import { useCMWFeatureFlagHierarchyQuery } from "@/_v2/api/cmw-feature-flag-hierarchy";
import { Alert, AlertDescription, AlertTitle } from "@/_v2/components/ui/alert";
import { Badge } from "@/_v2/components/ui/badge";
import { Button } from "@/_v2/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/_v2/components/ui/card";
import { Skeleton } from "@/_v2/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/_v2/components/ui/table";
import { cn } from "@/_v2/utils";

import {
  EntityRef,
  buildEntityLabelMaps,
  buildFlagInsight,
  buildOrgBreakdown,
} from "./lib";
import { CategoryBadge } from "./components/category-badge";
import { FlagStatusBadge } from "./components/flag-status-badge";
import { PageContainer } from "./components/page-container";
import { StatCard } from "./components/stat-card";

const GROUP_BADGE_LIMIT = 8;

// Tinted chips instead of solid badge variants — the solid green/red fills
// are hard to read at this density. The tokens aren't defined with
// <alpha-value>, so opacity modifiers like bg-success/15 don't work; use
// arbitrary values over the raw HSL vars instead.
const GROUP_BADGE_CLASSES = {
  success:
    "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.15)] text-success",
  destructive:
    "border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.15)] text-destructive",
};

const GroupBadgeList = ({
  groups,
  variant,
  expanded,
}: {
  groups: EntityRef[];
  variant: "success" | "destructive";
  expanded: boolean;
}) => {
  if (!groups.length) {
    return <span className="text-muted-foreground">—</span>;
  }

  const head = groups.slice(0, GROUP_BADGE_LIMIT);
  const extra = groups.slice(GROUP_BADGE_LIMIT);

  return (
    <div className="flex max-w-md flex-wrap gap-1">
      {head.map((group) => (
        <Badge
          key={group.id}
          variant="outline"
          className={cn("font-normal", GROUP_BADGE_CLASSES[variant])}
        >
          {group.label}
        </Badge>
      ))}
      {!expanded && extra.length ? (
        <Badge variant="outline" className="font-normal">
          +{extra.length} more
        </Badge>
      ) : null}
      {extra.length ? (
        // The expanding block is a full-width item of the SAME flex container
        // as the always-visible badges, so both sets share one left edge and
        // wrap rhythm. Height animation via the grid-rows 0fr -> 1fr trick:
        // the inner overflow-hidden wrapper lets the track collapse to zero,
        // so the row opens/closes with a real transition despite auto height.
        <div
          className={cn(
            "grid basis-full transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none",
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-wrap gap-1">
              {extra.map((group) => (
                <Badge
                  key={group.id}
                  variant="outline"
                  className={cn(
                    "font-normal transition-all duration-300 motion-reduce:transition-none",
                    GROUP_BADGE_CLASSES[variant],
                    expanded
                      ? "translate-y-0 opacity-100"
                      : "-translate-y-1 opacity-0"
                  )}
                >
                  {group.label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const FeatureFlagDetailPage = () => {
  const { flagName } = useParams<{ flagName: string }>();
  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<number>>(new Set());

  const toggleOrgExpanded = (orgId: number) => {
    setExpandedOrgIds((previous) => {
      const next = new Set(previous);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const { data: flags, isLoading: flagsLoading } = useCMWFeatureFlagsQuery();
  const { data: hierarchy, isLoading: hierarchyLoading } =
    useCMWFeatureFlagHierarchyQuery({});

  const flag = useMemo(
    () => (flags || []).find((candidate) => candidate.name === flagName),
    [flags, flagName]
  );
  const insight = useMemo(() => (flag ? buildFlagInsight(flag) : null), [flag]);

  const { orgLabels, groupLabels } = useMemo(
    () => buildEntityLabelMaps(flags || []),
    [flags]
  );
  const breakdown = useMemo(
    () =>
      flag
        ? buildOrgBreakdown(flagName, hierarchy, orgLabels, groupLabels)
        : [],
    [flag, flagName, hierarchy, orgLabels, groupLabels]
  );

  useDocumentTitle(
    insight
      ? `${insight.displayName} | Feature Flag Insights`
      : "Feature Flag Insights"
  );

  if (flagsLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (!insight) {
    return (
      <PageContainer>
        <h1 className="text-2xl font-extrabold tracking-tight">
          Unknown feature flag
        </h1>
        <p className="text-sm text-muted-foreground">
          CMW does not report a feature flag named{" "}
          <span className="font-mono">{flagName}</span>.
        </p>
        <Button variant="outline" asChild>
          <Link to="/v2/feature-flag-insights">
            Back to Feature Flag Insights
          </Link>
        </Button>
      </PageContainer>
    );
  }

  const topAdopter = breakdown.find((row) => row.enabledGroups.length > 0);
  const totalDisables =
    insight.disabledOrganizations.length +
    insight.disabledGroups.length +
    insight.disabledUsers.length;

  return (
    <PageContainer>
      <div>
        <Link
          to="/v2/feature-flag-insights"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Feature Flag Insights
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                {insight.displayName}
              </h1>
              <FlagStatusBadge status={insight.status} />
              <CategoryBadge category={insight.category} />
            </div>
            <div className="mt-1 font-mono text-sm text-muted-foreground">
              {insight.name}
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {insight.description ||
                "No description in the catalog yet. Check control.smartrent.com for the code that gates this flag."}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/feature-flags">Open Flag Manager</Link>
          </Button>
        </div>
      </div>

      {insight.hasExceptions ? (
        <Alert className="border-warning">
          <AlertTitle className="text-warning">
            Globally enabled with exceptions
          </AlertTitle>
          <AlertDescription>
            This flag is on for everyone, but has {totalDisables} explicit
            disable{totalDisables === 1 ? "" : "s"}:{" "}
            {[
              insight.disabledOrganizations.length &&
                `${insight.disabledOrganizations.length} org(s)`,
              insight.disabledGroups.length &&
                `${insight.disabledGroups.length} group(s)`,
              insight.disabledUsers.length &&
                `${insight.disabledUsers.length} user(s)`,
            ]
              .filter(Boolean)
              .join(", ")}
            . Those actors do <strong>not</strong> see the feature.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Organizations Enabled"
          value={insight.enabledOrganizations.length}
          accent="success"
          caption={
            insight.disabledOrganizations.length
              ? `${insight.disabledOrganizations.length} explicitly disabled`
              : undefined
          }
        />
        <StatCard
          title="Groups Enabled"
          value={insight.enabledGroups.length}
          accent="info"
          caption={
            insight.disabledGroups.length
              ? `${insight.disabledGroups.length} explicitly disabled`
              : undefined
          }
        />
        <StatCard
          title="User Overrides"
          value={insight.enabledUsers.length + insight.disabledUsers.length}
          accent="warning"
          caption={`${insight.enabledUsers.length} enabled / ${insight.disabledUsers.length} disabled`}
        />
        <StatCard
          title="Top Adopter"
          accent="brand"
          value={
            topAdopter ? (
              <Link
                to={`/organizations/${topAdopter.orgId}`}
                className="hover:underline"
              >
                {topAdopter.orgLabel}
              </Link>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          }
          caption={
            topAdopter
              ? `${topAdopter.enabledGroups.length} group(s) enabled`
              : "No org has group-level enablement"
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Breakdown by Organization</CardTitle>
        </CardHeader>
        <CardContent>
          {hierarchyLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : breakdown.length ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Org-Level Gate</TableHead>
                    <TableHead>Enabled Groups</TableHead>
                    <TableHead>Disabled Groups</TableHead>
                    <TableHead>User Overrides</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdown.map((row) => {
                    const expandable =
                      row.enabledGroups.length > GROUP_BADGE_LIMIT ||
                      row.disabledGroups.length > GROUP_BADGE_LIMIT;
                    const expanded = expandedOrgIds.has(row.orgId);

                    return (
                      <TableRow
                        key={row.orgId}
                        onClick={
                          expandable
                            ? () => toggleOrgExpanded(row.orgId)
                            : undefined
                        }
                        className={expandable ? "cursor-pointer" : undefined}
                      >
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {expandable ? (
                              <ChevronRightIcon
                                className={cn(
                                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
                                  expanded && "rotate-90"
                                )}
                              />
                            ) : null}
                            <Link
                              to={`/organizations/${row.orgId}`}
                              className="font-medium hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {row.orgLabel}
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell>
                          {row.orgGate === true ? (
                            <Badge variant="success">Enabled</Badge>
                          ) : row.orgGate === false ? (
                            <Badge variant="destructive">Disabled</Badge>
                          ) : (
                            <span
                              className="text-muted-foreground"
                              title="No org-level gate; groups/users below are gated individually"
                            >
                              {insight.globallyEnabled
                                ? "Inherits global (on)"
                                : "—"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <GroupBadgeList
                            groups={row.enabledGroups}
                            variant="success"
                            expanded={expanded}
                          />
                        </TableCell>
                        <TableCell>
                          <GroupBadgeList
                            groups={row.disabledGroups}
                            variant="destructive"
                            expanded={expanded}
                          />
                        </TableCell>
                        <TableCell>
                          {row.enabledUserOverrides +
                          row.disabledUserOverrides ? (
                            <span>
                              {row.enabledUserOverrides} on /{" "}
                              {row.disabledUserOverrides} off
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {insight.globallyEnabled
                ? "No org-, group-, or user-level gates — this flag is simply on for everyone via the global gate."
                : "No org-, group-, or user-level gates exist for this flag."}
            </p>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
};
